import React, { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation, Link } from 'react-router-dom';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '../components/table/DataTable';
import LogoSpinner from '../components/ui/LogoSpinner';
import Alert from '../components/ui/Alert';
import EmptyState from '../components/ui/EmptyState';
import Panel from '../components/ui/Panel';
import Reveal from '../components/ui/Reveal';
import { formatEventDate, formatTimestamp } from '../utils/date-utils';
import { lineLabel, productOfItemType, summaryRows } from './itemTypes';
import { variantLabel } from '../Parts/bomsApi';
import useUserStore from '../stores/useUserStore';
import { maySeeSalesPrices, formatMoney, lineAmount, orderTotal, totalCaveat } from './salesPrices';
import PageLayout from '../components/ui/PageLayout';
import { buttonClass } from '../components/ui/Button';
import BackLink from '../components/BackLink';
import { Field } from '../components/ui/Field';
import Tooltip from '../components/ui/Tooltip';
import StatusPill, { OrderTypeTag } from './StatusPill';
import JourneyStrip from './JourneyStrip';
import ReadingBadge from './ReadingBadge';
import ShipmentsSection from './ShipmentsSection';
import ShipDrawer from './ShipDrawer';
import { shipMissing, missingLinesText, shipErrorMessage, returnReasonRequired } from './shipping';
import CloseAsReturnedPanel from './CloseAsReturnedPanel';
import type { StillOut } from './CloseAsReturnedPanel';
import type { Journey, ShipDirection, Shipping, ShipResult } from './shipping';
import type { SalesOrder } from '../Customers/types';

/* Data row for key-value pairs */
const DataRow: React.FC<{ label: string; children?: React.ReactNode; border?: boolean }> = ({ label, children, border }) => (
  <div className={`flex justify-between items-baseline py-1.5 ${border ? 'border-t border-gray-700/50 mt-1 pt-2.5' : ''}`}>
    <span className="text-on-surface-variant text-sm">{label}</span>
    <span className="text-on-surface text-sm text-right">{children}</span>
  </div>
);

// ---------------------------------------------------------------------------
// The line-items table, as shared-DataTable columns.
//
// Module scope per the DataTable contract: the array never changes identity and
// every cell is pure over its row, so nothing here closes over page state. No
// `sortable` and no accessorFn — an order's lines are read in DOCUMENT order,
// and a re-sorted order is a different quote.
//
// Alignment and tabular figures come from `meta`, never from a class string,
// and the vessel owns the td: only the page's own per-column text tone rides
// `meta.cellClass`.
// ---------------------------------------------------------------------------

/** Price columns exist only for people who may see prices. Two frozen objects
 * rather than one built per render: a fresh identity would invalidate
 * tanstack's per-row visible-cell memo every pass, and this is a binary. */
const PRICE_COLUMNS_ON = { rate: true, amount: true };
const PRICE_COLUMNS_OFF = { rate: false, amount: false };

/** The order's total, computed off the table's own row model: a columnDef
 * footer gets a HeaderContext, so there is no row in scope and a module-scope
 * footer cannot close over the page's `totals`. It calls the page's OWN
 * `orderTotal` on the same lines in the same order, so the footer figure and
 * the page's `totals` object cannot state different numbers. */
const rowModelTotals = (ctx: { table: { getRowModel: () => { rows: { original: OrderLine }[] } } }) => orderTotal(ctx.table.getRowModel().rows.map((r) => r.original));

interface OrderLine {
  id: string;
  item_type: string;
  quantity: number;
  notes?: string | null;
  product_name?: string | null;
  variant?: string | null;
  unit_price_text?: string | null;
}

/** The order as GET /orders/:id returns it: the list row plus the detail-only fields. */
interface OrderDetail extends Omit<SalesOrder, 'lines'> {
  lines?: OrderLine[];
  tracking_number?: string | null;
}

const LINE_COLUMNS: ColumnDef<OrderLine, any>[] = [
  {
    id: 'item',
    header: 'Item',
    meta: { cellClass: 'text-white' },
    // The product the line sells (migration 129) rides under the item type; a
    // stock line with none says so quietly so the sale can be mapped before it ships.
    // A drone line is named by its model, so only its build rides underneath.
    cell: ({ row }) => {
      const { item_type, product_name, variant } = row.original;
      const byModel = productOfItemType(item_type) === 'model';
      const under = byModel
        ? (variant ? variantLabel(variant) : '')
        : (product_name ? `${product_name}${variant ? `, ${variantLabel(variant)}` : ''}` : '');
      return (
        <div>
          <div>{lineLabel(item_type, product_name)}</div>
          {under ? <div className="text-xs text-on-surface-variant">{under}</div> : null}
        </div>
      );
    },
  },
  {
    id: 'quantity',
    header: 'Qty',
    meta: { align: 'right', numeric: true, cellClass: 'text-white font-medium' },
    cell: ({ row }) => row.original.quantity,
  },
  {
    id: 'rate',
    header: 'Rate',
    // Verbatim, because `unit_price_text` is free text until ERP chunk 4: "TBD"
    // is a legitimate state of a deal, so the cell prints what was typed.
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.unit_price_text || '\u2014',
  },
  {
    id: 'amount',
    header: 'Amount',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => {
      const amount = lineAmount(row.original);
      return amount == null ? '\u2014' : formatMoney(amount);
    },
  },
  {
    id: 'notes',
    header: 'Notes',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.notes || '\u2014',
  },
];

/** The same columns wearing the total. TWO arrays rather than one built per
 * render, because contract 1 forbids rebuilding `columns` to add or drop a
 * footer, and an order where no line could be parsed has no total to state.
 *
 * A footer cell cannot span, so the old `colSpan={2}` pair becomes label plus
 * figure: the label lands in Rate, the column immediately left of the figure,
 * right-aligned by that column's own `meta.align` so it hugs the number. */
const LINE_COLUMNS_TOTALED: ColumnDef<OrderLine, any>[] = LINE_COLUMNS.map((col) => {
  if (col.id === 'rate') {
    return { ...col, footer: () => <span className="font-normal text-on-surface-variant">Total</span> };
  }
  if (col.id === 'amount') {
    return { ...col, footer: (ctx: any) => formatMoney(rowModelTotals(ctx).total) };
  }
  return col;
});

const SalesOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const location = useLocation();
  const api = useApi();
  const toast = useToast();
  const user = useUserStore(st => st.user);
  const mayShip = !!(user?.isadmin || user?.can_admin || user?.can_sales || user?.can_operations);
  function shipOffered(o: { status: string }) {
    return mayShip && !['pending', 'shipped', 'cancelled', 'returned'].includes(o.status);
  }
  const [order, setOrder] = useState<OrderDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingShipDate, setEditingShipDate] = useState(false);
  const [shipDateDraft, setShipDateDraft] = useState('');
  const [savingShipDate, setSavingShipDate] = useState(false);
  const [shipping, setShipping] = useState<Shipping | null>(null);
  const [journey, setJourney] = useState<Journey | null>(null);
  // 'out' | 'return' | null: which way the ship drawer is open.
  const [drawer, setDrawer] = useState<ShipDirection | null>(null);
  const [wantShip, setWantShip] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  // What is still out when Mark Returned was refused for want of a reason; null keeps the panel closed.
  const [closeMissing, setCloseMissing] = useState<StillOut[] | null>(null);

  // The reading and the journey are cheap and move together, so every write
  // that can change one (a shipment, a status move) refreshes both.
  const loadShipping = async () => {
    try {
      const [s, j] = await Promise.all([api.get(`/orders/${id}/shipping`), api.get(`/orders/${id}/journey`)]);
      setShipping(s);
      setJourney(j);
      return s;
    } catch (error) {
      toast.error('Shipping did not load. Refresh to try again.');
      return null;
    }
  };

  // ?ship=1 is a link from a work order: open the drawer empty, once, when
  // the order's shipping is in. Nothing is pre-filled.
  useEffect(() => {
    (async () => {
      const s = await loadShipping();
      if (s && new URLSearchParams(location.search).get('ship') === '1') setWantShip(true);
    })();
  }, [id]);

  // Opened once the order is in, and only where the Ship button would be.
  useEffect(() => {
    if (!wantShip || !order) return;
    setWantShip(false);
    if (shipOffered(order)) setDrawer('out');
    else toast.error(order.status === 'pending' ? 'Confirm this order before shipping on it.' : 'Nothing more can ship on this order.');
  }, [wantShip, order]);

  const closeDrawer = () => {
    setDrawer(null);
    if (new URLSearchParams(location.search).has('ship')) history.replace(location.pathname);
  };

  const handleRecorded = (result: ShipResult) => {
    setShipping(result.shipping);
    if (result.status) setOrder(prev => (prev ? { ...prev, status: result.status as string } : prev));
    loadShipping();
  };

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const data = await api.get(`/orders/${id}`);
        setOrder(data);
      } catch (error) {
        toast.error('Order not found');
        history.push('/orders#sale');
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [id]);

  const handleDelete = async () => {
    if (!order || !window.confirm(`Delete ${order.so_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/orders/${id}`);
      toast.success('Order deleted');
      history.push(`/orders#${order.order_type}`);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleSaveShipDate = async () => {
    setSavingShipDate(true);
    try {
      const updated = await api.patch(`/orders/${id}`, {
        body: { ship_date: shipDateDraft || null },
      });
      setOrder(prev => (prev ? { ...prev, ...updated } : prev));
      toast.success(shipDateDraft ? 'Ship date updated' : 'Ship date cleared');
      setEditingShipDate(false);
    } catch (error) {
      toast.error('Failed to update ship date');
    } finally {
      setSavingShipDate(false);
    }
  };

  const startEditShipDate = () => {
    if (!order) return;
    setShipDateDraft(order.ship_date ? order.ship_date.split('T')[0] : '');
    setEditingShipDate(true);
  };

  const handleStatusChange = async (newStatus: string, reason?: string) => {
    setStatusError(null);
    try {
      const body: { status: string; reason?: string } = { status: newStatus };
      if (reason) body.reason = reason;
      const updated = await api.patch(`/orders/${id}`, { body });
      setOrder(prev => (prev ? { ...prev, ...updated } : prev));
      toast.success(`Status changed to ${newStatus.replace('_', ' ')}`);
      setCloseMissing(null);
      loadShipping();
    } catch (error: any) {
      const stillOut = newStatus === 'returned' && !reason ? returnReasonRequired(error) : null;
      if (stillOut) {
        setCloseMissing(stillOut.missing);
        return;
      }
      // A lease or rental's hand move to delivered is refused until every line has gone out: say which ones.
      const missing = shipMissing(error);
      const code = error?.data?.error?.code;
      if (missing.length > 0) {
        setStatusError(`Not every line has gone out yet: ${missingLinesText(missing)}. Use Ship to record them first.`);
      } else if (code === 'not_covered' || code === 'status_set_by_data') {
        setStatusError(shipErrorMessage(error));
      } else {
        toast.error('Failed to update status');
      }
    }
  };

  if (loading) return <LogoSpinner label="Loading the order" />;
  if (!order) return null;

  // Every other action button sits on a status that Cancel also covers.
  const hasActions = !['cancelled', 'shipped', 'returned'].includes(order.status);

  const lines = order.lines || [];
  // Catalog order, grouped types combined, zeros omitted. On Firefleet this is
  // the same five rows as before; on SwarmSystems it is that product's own items.
  const summary = summaryRows(lines);

  // The API already stripped prices for anyone who may not see them; this only
  // decides whether to render the columns. Mirrored in sales-price-visibility.js.
  const mayPrice = maySeeSalesPrices(user);
  const totals = orderTotal(lines);
  const totalNote = totalCaveat(totals);
  // The old tfoot's own gate, unchanged: nothing parsed and something left out
  // means there is no total worth stating. It decides which columns array the
  // table wears, since a footer cannot be added or dropped per render.
  const showTotals = mayPrice && (totals.total > 0 || totals.complete);

  // Mirrors the API's gate on recording a shipment: sales or operations.


  return (
    <PageLayout width="narrow" kind="record" className="pt-6 space-y-5">

      <BackLink to={`/orders#${order.order_type}`} label="Orders" />

      {/* ---- HEADER CARD ---- */}
      <Panel label="Order" accent="primary" lit>
        <div className="pt-1">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div className="min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight leading-tight font-mono">
                {order.so_number}
              </h1>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                {['sale', 'lease', 'rental'].includes(order.order_type) && (
                  <OrderTypeTag type={order.order_type} size="md" />
                )}
                {/* Part shipped reads as the status itself, and so does a sale with a return ("Shipped, 1 returned");
                    the stored status stays underneath. A lease keeps its pill, which carries the round trip's stages. */}
                {shipping?.reading?.state === 'partly' || (order.order_type === 'sale' && (shipping?.reading?.returned ?? 0) > 0) ? (
                  <ReadingBadge reading={shipping?.reading} />
                ) : (
                  <StatusPill status={order.status} size="md" />
                )}
              </div>
            </div>

            {/* Created date callout on larger screens */}
            <div className="hidden sm:flex flex-col items-end flex-shrink-0">
              <span className="text-[11px] font-semibold uppercase tracking-widest text-on-surface-variant">Created</span>
              <span className="text-lg font-semibold text-white mt-0.5">
                {formatTimestamp(order.created_at)}
              </span>
            </div>
          </div>
        </div>

        <div className="my-4" />

        {/* Action buttons row */}
        <div className="flex flex-wrap gap-2">
          <Link
            to={`/orders/${id}/edit`}
            className={buttonClass('accent', 'md')}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" /></svg>
            Edit
          </Link>
          {order.status && !['pending', 'cancelled', 'returned'].includes(order.status) && (
            <Link
              to={`/orders/${id}/fulfillment`}
              className={buttonClass('purple', 'md')}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" /></svg>
              Fulfillment
            </Link>
          )}
          <div className="flex-1" />
          <button
            onClick={handleDelete}
            className={buttonClass('danger-quiet', 'md')}
          >
            Delete
          </button>
        </div>
      </Panel>

      {journey && <JourneyStrip journey={journey} />}

      {/* ---- SHIPPING ---- */}
      <Panel title="Shipping" titleStyle="section" accent="primary">
        <div className="space-y-3">
          {/* Ship date row — inline edit, independent of full order edit */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-on-surface-variant text-sm">Ship Date</span>
            {editingShipDate ? (
              <div className="flex items-center gap-2">
                <Field
                  type="date"
                  aria-label="Ship date"
                  value={shipDateDraft}
                  onChange={e => setShipDateDraft(e.target.value)}
                  className="!w-auto"
                />
                <button
                  onClick={handleSaveShipDate}
                  disabled={savingShipDate}
                  className={buttonClass('quiet', 'sm')}
                >
                  {savingShipDate ? 'Saving\u2026' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingShipDate(false)}
                  disabled={savingShipDate}
                  className={buttonClass('quiet', 'sm')}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <span className={`text-sm ${order.ship_date ? 'text-on-surface' : 'text-on-surface-variant italic'}`}>
                  {order.ship_date ? formatEventDate(order.ship_date) : 'Not yet set'}
                </span>
                <button
                  onClick={startEditShipDate}
                  className={buttonClass('quiet', 'sm')}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125" />
                  </svg>
                  {order.ship_date ? 'Edit' : 'Set'}
                </button>
              </div>
            )}
          </div>
          {/* Tracking — read-only here; updated via the 'Ship' status button */}
          {order.tracking_number && (
            <div className="flex items-center justify-between gap-3 flex-wrap border-t border-gray-700/40 pt-3">
              <span className="text-on-surface-variant text-sm">Tracking</span>
              <a
                href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(order.tracking_number)}`}
                target="_blank" rel="noopener noreferrer"
                className="text-blue-400 hover:text-blue-300 text-sm font-mono transition-colors duration-150"
              >
                {order.tracking_number}
              </a>
            </div>
          )}
        </div>
      </Panel>

      {/* ---- SHIPMENTS ---- */}
      {shipping && (
        <ShipmentsSection
          shipping={shipping}
          showBack={order.order_type === 'lease' || order.order_type === 'rental' || shipping.lines.some(l => l.returned > 0)}
          onShip={shipOffered(order) ? () => setDrawer('out') : undefined}
          onReturn={mayShip && shipping.lines.some(l => l.out - l.returned > 0) ? () => setDrawer('return') : undefined}
        />
      )}

      {/* ---- DETAILS GRID ---- */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

        {/* Order Details */}
        <Panel title="Order Details" titleStyle="section" accent="primary">
          <div className="space-y-0">
            <DataRow label="Customer">
              {order.customer_org_id ? (
                <Link to={`/customers/${order.customer_org_id}`} className="text-blue-400 hover:text-blue-300 transition-colors duration-150">
                  {order.org_name || order.customer_name}
                </Link>
              ) : (order.customer_name || '\u2014')}
            </DataRow>
            {order.order_type === 'sale' && (
              <DataRow label="Delivery Date">{order.delivery_date ? formatEventDate(order.delivery_date) : '\u2014'}</DataRow>
            )}
            {(order.order_type === 'lease' || order.order_type === 'rental') && (
              <>
                <DataRow label="Effective Date">{order.effective_date ? formatEventDate(order.effective_date) : '\u2014'}</DataRow>
                <DataRow label="End Date">
                  {order.end_date
                    ? formatEventDate(order.end_date)
                    : <span className="text-on-surface-variant italic">Permanent</span>}
                </DataRow>
                {order.return_date && (
                  <DataRow label="Expected Return">{formatEventDate(order.return_date)}</DataRow>
                )}
              </>
            )}
            <DataRow label="Created">{formatTimestamp(order.created_at)}</DataRow>
            <DataRow label="Created By">{order.creator_firstname || '\u2014'}</DataRow>
          </div>
        </Panel>

        {/* Summary */}
        <Panel title="Summary" titleStyle="section" accent="primary">
          <div className="space-y-0">
            {summary.map(row => (
              <DataRow key={row.label} label={row.label}>
                {row.emphasis ? <span className="text-white font-bold text-lg">{row.total}</span> : row.total}
              </DataRow>
            ))}
            <DataRow label="Line Items" border>{lines.length}</DataRow>
          </div>
        </Panel>
      </div>

      {/* ---- NOTES ---- */}
      {order.notes && (
        <Panel title="Notes" titleStyle="section" accent="primary">
          <p className="text-on-surface text-sm whitespace-pre-wrap leading-relaxed">{order.notes}</p>
        </Panel>
      )}

      {/* ---- LINE ITEMS ---- */}
      {/* Prices are free text until ERP chunk 4, so a line can read "TBD". The caveat names what
          the sum left out rather than printing a confident number for the rest. It sits in the
          panel's footer because a table footer cell cannot span the row, and a sentence about the
          total is not a second total. */}
      <Panel
        title="Line Items"
        titleStyle="section"
        accent="primary"
        padding="none"
        footer={showTotals && totalNote ? <p className="m-0 text-right text-xs text-on-surface-variant">{totalNote}</p> : undefined}
      >
        <DataTable
          data={lines}
          columns={showTotals ? LINE_COLUMNS_TOTALED : LINE_COLUMNS}
          getRowId={line => String(line.id)}
          columnVisibility={mayPrice ? PRICE_COLUMNS_ON : PRICE_COLUMNS_OFF}
          on="panel"
          empty={
            <EmptyState variant="area">
              No line items yet. <Link to={`/orders/${id}/edit`} className="text-primary hover:text-primary/80 transition-colors duration-150">Add some</Link>
            </EmptyState>
          }
          emptyMode="replace"
        />
      </Panel>

      {/* ---- STATUS ACTIONS ---- */}
      {hasActions && (
      <Panel title="Actions" titleStyle="section" accent="primary">
        <div className="flex flex-wrap gap-3">
          {/* Sale workflow: pending → confirmed, then production and shipping move the status from data */}
          {order.order_type === 'sale' && (
            <>
              {order.status === 'pending' && (
                <button onClick={() => handleStatusChange('confirmed')}
                  className={buttonClass('info', 'md')}>
                  Confirm Order
                </button>
              )}
            </>
          )}

          {/* Lease/Rental workflow: pending → confirmed, then the dock scans move it to shipping and back to returned. Delivered and Awaiting return are a person's click. */}
          {(order.order_type === 'lease' || order.order_type === 'rental') && (
            <>
              {order.status === 'pending' && (
                <button onClick={() => handleStatusChange('confirmed')}
                  className={buttonClass('info', 'md')}>
                  Confirm Order
                </button>
              )}
              {order.status === 'shipping' && (
                <button onClick={() => handleStatusChange('delivered')}
                  className={buttonClass('good', 'md')}>
                  Mark Delivered
                </button>
              )}
              {order.status === 'confirmed' && shipping && shipping.shipments.length === 0 && (
                <Tooltip label="For orders moved by hand before shipments were recorded here">
                  <button onClick={() => handleStatusChange('shipping')}
                    className={buttonClass('warn', 'md')}>
                    Mark Shipping
                  </button>
                </Tooltip>
              )}
              {order.status === 'delivered' && order.end_date && (
                <button onClick={() => handleStatusChange('awaiting_return')}
                  className={buttonClass('warn', 'md')}>
                  Awaiting Return
                </button>
              )}
              {order.status === 'awaiting_return' && (
                <button onClick={() => handleStatusChange('returned')}
                  className={buttonClass('good', 'md')}>
                  Mark Returned
                </button>
              )}
            </>
          )}

          {/* Cancel — available for all types unless already terminal */}
          {!['cancelled', 'shipped', 'returned'].includes(order.status) && (
            <button onClick={() => handleStatusChange('cancelled')}
              className={buttonClass('danger-quiet', 'md')}>
              Cancel Order
            </button>
          )}
        </div>
        <Reveal show={!!statusError}>
          <Alert variant="text" tone="error" className="mt-3">{statusError}</Alert>
        </Reveal>
        <Reveal show={!!closeMissing}>
          <CloseAsReturnedPanel
            missing={closeMissing ?? []}
            onClose={(reason) => handleStatusChange('returned', reason)}
            onCancel={() => setCloseMissing(null)}
          />
        </Reveal>
      </Panel>
      )}

      {drawer && shipping && (
        <ShipDrawer
          order={{ id, so_number: order.so_number }}
          shipping={shipping}
          direction={drawer}
          onClose={closeDrawer}
          onRecorded={handleRecorded}
        />
      )}
    </PageLayout>
  );
};

export default SalesOrderDetail;
